import Box from "@mui/material/Box";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AUDIT_TOKENS } from "../../theme/auditTokens";
import { visuallyHidden } from "../../theme/visuallyHidden";
import { AuditButton } from "../common/AuditButton";
import { formatNumber } from "@/utilities/formatNumber";

const IMPRINT_URL = "https://fairfleet.de/impressum/";

export interface LoadMoreProps {
  /** Vehicles whose data is loaded. */
  shown: number;
  total: number;
  canLoadMore: boolean;
  /** Vehicles the next click adds. */
  nextStep: number;
  onLoadMore: () => void;
  /** Left part of the bar: the status line or the load progress. */
  status?: ReactNode;
  /** Entries of the view after the filters, e.g. "79 of 80 entries". */
  entries?: string;
}

/**
 * The single line under the rows: status on the left, then the entry count, then "x of y
 * vehicles" with the button that loads the next ones, and the imprint link. Wraps on narrow screens.
 */
export function LoadMoreBar({ shown, total, canLoadMore, nextStep, onLoadMore, status, entries }: LoadMoreProps) {
  const { t, i18n } = useTranslation();
  const number = (value: number) => formatNumber(value, i18n.language);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: "16px",
        rowGap: "4px",
        minHeight: 40,
        p: "4px 12px",
        borderTop: `1px solid ${AUDIT_TOKENS.line}`,
        fontSize: 12,
        color: AUDIT_TOKENS.muted,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>{status}</Box>
      {entries && <span>{entries}</span>}
      {total > 0 && (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
          <span role="status">{t("logbookAudit.loadMore.count", { shown: number(shown), total: number(total) })}</span>
          {canLoadMore && (
            <AuditButton onClick={onLoadMore} sx={{ py: "5px", px: "10px", fontSize: 12.5, "@media (max-width: 820px)": { flex: "0 0 auto" } }}>
              {t("logbookAudit.loadMore.button", { count: nextStep })}
            </AuditButton>
          )}
        </Box>
      )}
      <Box
        component="a"
        href={IMPRINT_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: AUDIT_TOKENS.muted,
          textUnderlineOffset: "2px",
          "&:hover": { color: AUDIT_TOKENS.text },
          "&:focus-visible": { outline: AUDIT_TOKENS.focusRing, outlineOffset: 1 },
        }}
      >
        {t("logbookAudit.meta.imprint")}
        <Box component="span" sx={visuallyHidden}>
          {t("logbookAudit.meta.newTab")}
        </Box>
      </Box>
    </Box>
  );
}
